import Konva from 'konva';

// CleanShot-style endpoint editor for 2-point shapes (Konva.Line / Konva.Arrow).
// Replaces the standard 8-handle AABB transformer with a pair of draggable
// circles sitting on the actual geometry, so the selection hugs the line.
//
// The handles live on `engine.uiLayer` (the same layer the transformer uses)
// so they always sit above content, and they translate handle-absolute coords
// back to node-local coords before writing to `node.points()`.
export class EndpointHandles {
  constructor(engine) {
    this.engine = engine;
    this.layer = engine.uiLayer;
    this.node = null;
    this._handles = [];
    this._dragStart = null;
    this._onNodeChange = null;
  }

  attach(node) {
    this.detach();
    this.node = node;
    this._build();
    this._sync();

    // Re-sync when the user drags the whole line (node.x / node.y change), or
    // when the points array is rewritten (live property update, undo/redo).
    this._onNodeChange = () => this._sync();
    node.on('dragmove.endpoints xChange.endpoints yChange.endpoints pointsChange.endpoints', this._onNodeChange);
  }

  detach() {
    if (!this.node) return;
    this.node.off('.endpoints');
    this._onNodeChange = null;
    for (const h of this._handles) h.destroy();
    this._handles = [];
    this.node = null;
    this.layer.batchDraw();
  }

  setVisible(v) {
    for (const h of this._handles) h.visible(v);
    this.layer.batchDraw();
  }

  refresh() {
    this._sync();
  }

  _build() {
    for (let i = 0; i < 2; i++) {
      const handle = new Konva.Circle({
        radius: 6,
        fill: '#FFFFFF',
        stroke: '#155EEF',
        strokeWidth: 1.5,
        draggable: true,
        hitStrokeWidth: 14,
        name: `endpoint-${i}`,
      });

      // Don't let the stage's pointerdown handler treat this as a "click on
      // empty canvas" and deselect — the handle isn't a stage hit, it's UI.
      handle.on('pointerdown', (e) => { e.cancelBubble = true; });

      handle.on('dragstart', () => {
        this._dragStart = [...this.node.points()];
        this.engine.setCursor('grabbing');
      });

      handle.on('dragmove', () => {
        const abs = handle.getAbsolutePosition();
        const local = this._absToLocal(abs);
        const pts = [...this.node.points()];
        pts[i * 2] = local.x;
        pts[i * 2 + 1] = local.y;
        this.node.points(pts);
        this.engine.contentLayer.batchDraw();
      });

      handle.on('dragend', () => {
        const before = this._dragStart;
        const after = [...this.node.points()];
        this._dragStart = null;
        this.engine.setCursor(null);
        if (!before) return;
        if (before.length !== after.length || before.some((v, idx) => v !== after[idx])) {
          const node = this.node;
          this.engine.pushOp({
            undo: () => { node.points(before); this.engine.contentLayer.batchDraw(); this.refresh(); },
            redo: () => { node.points(after);  this.engine.contentLayer.batchDraw(); this.refresh(); },
          });
        }
      });

      handle.on('mouseenter', () => this.engine.setCursor('grab'));
      handle.on('mouseleave', () => { if (!handle.isDragging()) this.engine.setCursor(null); });

      this.layer.add(handle);
      this._handles.push(handle);
    }
  }

  _sync() {
    if (!this.node || this._handles.length !== 2) return;
    const points = this.node.points();
    if (!points || points.length < 4) return;
    const nodeAbs = this.node.getAbsolutePosition();
    for (let i = 0; i < 2; i++) {
      const x = nodeAbs.x + points[i * 2];
      const y = nodeAbs.y + points[i * 2 + 1];
      this._handles[i].position({ x, y });
    }
    this.layer.batchDraw();
  }

  _absToLocal(abs) {
    const nodeAbs = this.node.getAbsolutePosition();
    return { x: abs.x - nodeAbs.x, y: abs.y - nodeAbs.y };
  }
}
